import { ChangeEvent, useMemo, VFC } from 'react';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import ReactSelect, { OptionType, ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionZendeskTicketCreateV1 } from 'dashboard/models/workflow-version';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { JsonSchemaStringifiableTypes } from 'lib/fields';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

interface ParamsZendeskCreateTicketV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionZendeskTicketCreateV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionZendeskTicketCreateV1['params']) => void;
}

const SUPPORTED_TICKET_STATUS: Record<
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  WorkflowActionZendeskTicketCreateV1['params']['status'],
  string
> = {
  default: 'Default',
  new: 'New',
  open: 'Open',
  pending: 'Pending',
  hold: 'Hold',
  solved: 'Solved',
  closed: 'Closed',
};

const statusOptions: OptionType<WorkflowActionZendeskTicketCreateV1['params']['status']>[] =
  Object.entries(SUPPORTED_TICKET_STATUS).map(([value, label]) => ({
    value: value as WorkflowActionZendeskTicketCreateV1['params']['status'],
    label,
  }));

const ParamsZendeskCreateTicketV1: VFC<ParamsZendeskCreateTicketV1Props> = ({
  step,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const canViewApiKey = usePermission(Permission.ApiKeyView);
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);

  const updateZendeskTicketParams = <
    Key extends keyof WorkflowActionZendeskTicketCreateV1['params'],
  >(
    key: Key,
    value: WorkflowActionZendeskTicketCreateV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const handlePublicCheckbox = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    updateZendeskTicketParams('public', checked);
  };

  const tagOptions = (tagParam: string[]) => {
    return tagParam.map((tag) => {
      return { label: tag, value: tag };
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionZendeskTicketCreateV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Domain URL' htmlFor='params-zendesk-create-ticket__domain-url' />
      <Input
        value={params['domain-url']}
        onChange={(e) => updateZendeskTicketParams('domain-url', e.target.value)}
        disabled={!editable}
        placeholder='https://yourcompany.zendesk.com'
        id='params-zendesk-create-ticket__domain-url'
        hasError={hasErrorFor('domain-url')}
      />
      {renderErrorMessageFor('domain-url')}
      <Spacer height={2} />
      <ParamLabel labelText='Account Email' htmlFor='params-zendesk-create-ticket__account-email' />
      <Input
        value={params['account-email']}
        onChange={(e) => updateZendeskTicketParams('account-email', e.target.value)}
        disabled={!editable}
        placeholder='owner@yourcompany.com'
        id='params-zendesk-create-ticket__account-email'
        hasError={hasErrorFor('account-email')}
      />
      {renderErrorMessageFor('account-email')}
      <Spacer height={2} />
      <ParamLabel labelText='API Token' htmlFor='params-zendesk-create-ticket__api-token' />
      <Input
        value={canViewApiKey ? params['api-token'] : 'Key Hidden'}
        onChange={(e) => updateZendeskTicketParams('api-token', e.target.value)}
        disabled={!editable || !canViewApiKey}
        id='params-zendesk-create-ticket__api-token'
        hasError={hasErrorFor('api-token')}
      />
      {renderErrorMessageFor('api-token')}
      <Spacer height={2} />
      <ParamLabel labelText='Subject' htmlFor='params-zendesk-create-ticket__subject' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['subject']}
        updateValue={(value: string) => {
          updateZendeskTicketParams('subject', value);
        }}
        editable={editable}
        allowedTypes={JsonSchemaStringifiableTypes}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-zendesk-create-ticket__subject'
        hasError={hasErrorFor('subject')}
      />
      {renderErrorMessageFor('subject')}
      <Spacer height={2} />
      <ParamLabel labelText='Comment Body' htmlFor='params-zendesk-create-ticket__comment-body' />
      <HandlebarFormControl
        size='md'
        type='text-area'
        value={params['body']}
        updateValue={(value: string) => {
          updateZendeskTicketParams('body', value);
        }}
        editable={editable}
        allowedTypes={JsonSchemaStringifiableTypes}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-zendesk-create-ticket__comment-body'
        hasError={hasErrorFor('body')}
      />
      {renderErrorMessageFor('body')}
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        <Checkbox
          id='params-zendesk-create-ticket__make-public'
          label='Make the Comment Public'
          checked={params['public']}
          disabled={!editable}
          onChange={handlePublicCheckbox}
        />
        <Spacer height={2} />
        <ParamLabel labelText='Status' htmlFor='params-zendesk-create-ticket__status-input' />
        <ReactSelect
          value={params['status']}
          options={statusOptions}
          onChange={(value: WorkflowActionZendeskTicketCreateV1['params']['status']) => {
            updateZendeskTicketParams('status', value);
          }}
          id='params-zendesk-create-ticket__status'
          disabled={!editable}
        />
        <Spacer height={2} />
        <ParamLabel labelText='Add Tags' htmlFor='params-zendesk-create-ticket__add-tags-input' />
        <ReactCreatableSelect
          size='md'
          id='params-zendesk-create-ticket__add-tags'
          value={params['add-tags']}
          disabled={!editable}
          isMulti={true}
          isSearchable={true}
          portal={true}
          onChange={(value: string[]) => {
            updateZendeskTicketParams('add-tags', value);
          }}
          onCreateOption={(str: string) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            updateZendeskTicketParams('add-tags', params['add-tags'].concat(str));
          }}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          options={tagOptions(params['add-tags'])}
        />
        <Spacer height={2} />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};
export default ParamsZendeskCreateTicketV1;
